import { StarIcon, StarOffIcon } from "lucide-react";
import { Button } from "@capacit-com/lara-law-shared-ui";
import { Spinner } from "@capacit-com/lara-law-shared-ui";
import { FC, useState } from "react";
import { DocumentCategory } from "@/models/legislation/Legislation";
import { useFavoriteToggle } from "@/hooks/useFavoriteToggle";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@capacit-com/lara-law-shared-ui";

type FavoriteButtonProps = {
  isHovering: boolean;
  documentCategory: DocumentCategory;
  documentId: string;
  title: string;
  isFavorite: boolean;
  // For legislation
  datasource?: string;
  internalId?: string;
  // For workspace documents
  workspaceId?: string;
  className?: string;
  strokeWidth?: number;
  container?: HTMLElement;
  onOperationStart?: () => void;
  onOperationEnd?: () => void;
};

const FavoriteButton: FC<FavoriteButtonProps> = ({
  isHovering,
  documentCategory,
  documentId,
  title,
  isFavorite,
  datasource,
  internalId,
  workspaceId,
  className,
  strokeWidth = 2,
  container,
  onOperationStart,
  onOperationEnd,
}) => {
  const { toggle, isLoading, label } = useFavoriteToggle({
    documentCategory,
    documentId,
    title,
    isFavorite,
    datasource,
    internalId,
    workspaceId,
    onOperationStart,
    onOperationEnd,
  });

  const [isHoveringButton, setIsHoveringButton] = useState(false);

  const handleFavoriteClick = async (e: React.MouseEvent) => {
    e.stopPropagation();
    await toggle();
    setIsHoveringButton(false);
  };

  return (
    <>
      {(isHovering || isFavorite) && !isLoading && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={className + " p-2"}
              onClick={handleFavoriteClick}
              disabled={isLoading}
              onMouseEnter={() => setIsHoveringButton(true)}
              onMouseLeave={() => setIsHoveringButton(false)}
            >
              <>
                {isHoveringButton && isFavorite ? (
                  <StarOffIcon
                    className="size-4 fill-warning stroke-2"
                    strokeWidth={strokeWidth}
                  />
                ) : (
                  <StarIcon
                    className={`size-4 ${
                      isFavorite
                        ? "fill-warning stroke-warning"
                        : isHoveringButton
                          ? "fill-warning"
                          : ""
                    }`}
                    strokeWidth={strokeWidth}
                  />
                )}
              </>
            </Button>
          </TooltipTrigger>
          <TooltipContent container={container}>{label}</TooltipContent>
        </Tooltip>
      )}
      {isLoading && (
        <Button
          variant="ghost"
          size="icon"
          className={className + " p-2"}
          onClick={(e) => {
            e.stopPropagation();
          }}
        >
          <Spinner className="size-4" />
        </Button>
      )}
    </>
  );
};

export default FavoriteButton;
